import { CreatedType, OrderStatus } from "@/models/order/helpers";
import {
  LineItem,
  LineItemRecord,
  Order,
  OrderDetails
} from "@/models/order/order";
import {
  DriftReport,
  QuoteDraft,
  QuoteDraftPayload,
  toLineItem,
  toQuoteDraftLine
} from "@/models/quote_draft/quote_draft";
import { composeQuoteNotes, stripQuoteNotes } from "@/models/order/quote_notes";
import {
  clearCustomerDetailsPrefetch,
  prefetchCustomerDetails,
  refreshCustomerDetails
} from "@/services/customer_details_prefetch";
import QuoteDraftService from "@/services/quote_draft_service";
import { defaultState } from ".";
import { OrderContext, OrderMutations, OrderState } from "./types";

const quoteDraftService = new QuoteDraftService();

export const addLineItem = (context: OrderContext, value: LineItem) => {
  context.commit(OrderMutations.SetLineItem, value);
};

export const editLineItem = (
  context: OrderContext,
  value: { item: LineItem; index: number }
) => {
  context.commit(OrderMutations.EditLineItem, value);
};

export const replaceLineItems = (
  context: OrderContext,
  value: LineItem[] | LineItemRecord[] | []
) => {
  context.commit(OrderMutations.SetAllLineItems, value);
};

/**
 * FUPOF-191: stamps an explicit quote-wide multiplier onto every line and
 * releases any pinned to a NetSuite value — the rep picking a price level
 * outright is the one thing that overrides a multiplier accepted from NetSuite.
 *
 * The lines are written here rather than left to `Cart.loadCartPriceLevels`
 * because that only runs on the cart: saving from checkout would otherwise move
 * the global level while every line kept its old multiplier.
 */
export const applyQuoteMultiplier = (context: OrderContext, value: number) => {
  const items = (context.state.items ?? []) as LineItem[];
  if (!items.length) return;
  context.commit(
    OrderMutations.SetAllLineItems,
    items.map(item => {
      const next = { ...item, multiplier: value };
      delete next.ns_multiplier;
      return next;
    })
  );
};

export const addOrderStatus = (context: OrderContext, value: OrderStatus) => {
  context.commit(OrderMutations.SetStatus, value);
};

export const addShipping = (
  context: OrderContext,
  value: OrderDetails["shipping_details"]
) => {
  context.commit(OrderMutations.SetShipping, value);
};

export const addBilling = (
  context: OrderContext,
  value: OrderDetails["billing_details"]
) => {
  context.commit(OrderMutations.SetBilling, value);
};

export const addNotes = (context: OrderContext, value: string) => {
  context.commit(OrderMutations.SetNotes, value);
};

export const addPayment = (
  context: OrderContext,
  value: OrderDetails["payment_details"]
) => {
  context.commit(OrderMutations.SetPayment, value);
};

export const addContact = (
  context: OrderContext,
  value: OrderDetails["contact_details"]
) => {
  context.commit(OrderMutations.SetContact, value);
};

export const addOrder = (context: OrderContext, value: Order) => {
  context.commit(OrderMutations.SetOrder, value);
};

export const addOrderId = (
  context: OrderContext,
  value: { id: string }
) => {
  context.commit(OrderMutations.SetOrderId, value);
};

const cloneDefault = <T>(section: T): T =>
  section ? JSON.parse(JSON.stringify(section)) : section;

/**
 * See setOrder mutation.
 *
 * The header sections are deep-cloned out of `defaultState`: assigning those
 * objects by reference would leave the live store aliasing the module-level
 * singleton, so the next in-place edit would silently corrupt the "blank"
 * order for the rest of the page's life.
 */
export const clearOrder = (context: OrderContext) => {
  const blankOrder = Object.assign(
    {},
    {
      ...defaultState,
      contact_details: cloneDefault(defaultState.contact_details),
      shipping_details: cloneDefault(defaultState.shipping_details),
      billing_details: cloneDefault(defaultState.billing_details),
      payment_details: cloneDefault(defaultState.payment_details),
      notes: ""
    }
  );
  replaceLineItems(context, []);
  context.commit(OrderMutations.ClearOrder, blankOrder);
};

export const assignCustomer = (
  context: OrderContext,
  value: {
    customer_id: string;
    customer_name: string;
    customer_netsuite_id: number | undefined;
    customer_netsuite_contact_id: number | undefined;
    customer_netsuite_payment_terms_id: number | undefined;
    customer_netsuite_price_level_id: number | undefined;
    customer_netsuite_price_level_discount: number | undefined;
    order_id: string | null;
  }
) => {
  context.commit(OrderMutations.SetCustomer, value);
  // FUPOF-191: start pulling the customer's full NetSuite record now, in the
  // background. Checkout needs it before it can render, and this is the
  // earliest moment we know which customer to ask for — by the time the user
  // walks from the Cart to Quote Details it is usually already in hand, so the
  // "Obtaining Customer Details" overlay never has to appear.
  prefetchCustomerDetails(value.customer_netsuite_id);
};

export const clearCustomer = (context: OrderContext) => {
  clearCustomerDetailsPrefetch();
  context.commit(OrderMutations.ClearCustomer, null);
};

/**
 * FUPOF-191 (BIGGER-TWEAKS #2): full reset of the persisted order state when
 * the stored customer is invalid for the current user — either the user
 * changed (login / impersonation takeover / leaving impersonation) or they lost
 * access to the customer. Wipes the whole order, not just the customer: the
 * cart's lines are priced off the stale customer's price level, `draft_id` may
 * point at a quote owned by someone else, and the edit-mode banner keys off
 * draft_id/netsuite_id.
 *
 * Goes through ResetState rather than clearOrder + clearCustomer so the
 * in-flow fields those two leave behind (status, type, status_log,
 * cost_details, the NS sales-order link) go too — none of them mean anything
 * once the identity changes.
 *
 * Callers must also clear the price multiplier (it lives in the global module).
 * The PSQL draft row itself is untouched — it stays in its owner's Saved Quotes.
 */
export const resetForUserChange = (context: OrderContext) => {
  clearCustomerDetailsPrefetch();
  context.commit(OrderMutations.ResetState, {
    ...defaultState,
    contact_details: cloneDefault(defaultState.contact_details),
    shipping_details: cloneDefault(defaultState.shipping_details),
    billing_details: cloneDefault(defaultState.billing_details),
    payment_details: cloneDefault(defaultState.payment_details),
    status_log: [],
    items: []
  });
};

export const clearCreatedType = (context: OrderContext) => {
  context.commit(OrderMutations.ClearCreatedType, null);
};

/* ------------------------------------------------------------------ */
/* FUPOF-191: PSQL quote-draft lifecycle                               */
/* ------------------------------------------------------------------ */

const buildDraftPayload = (state: OrderState): QuoteDraftPayload => ({
  netsuite_customer_id: state.customer_netsuite_id ?? null,
  title:
    state.contact_details?.quote_name ||
    state.customer_name ||
    "Untitled Draft",
  header_payload: {
    contact_details: state.contact_details,
    shipping_details: state.shipping_details,
    billing_details: state.billing_details,
    payment_details: state.payment_details,
    // Persisted composed, for the same reason `quote_lines.notes` is (see
    // `line_notes.ts`): the API compares the draft's header to NetSuite
    // directly, and NetSuite holds the composed string.
    notes: composeQuoteNotes(state.notes, (state.items ?? []) as LineItem[]),
    cost_details: state.cost_details,
    // FUPOF-209: the last shipping + tax a reload read off NetSuite. Persisted so a
    // reopened quote shows them immediately, instead of falling back to TBD until the
    // NS reload returns (or staying TBD when that read fails). NetSuite still owns the
    // values — the next successful reload overwrites this, and nothing here is ever
    // sent back to NetSuite on submit.
    ns_totals: state.ns_totals ?? null,
    customer_id: state.customer_id,
    customer_name: state.customer_name,
    customer_netsuite_contact_id: state.customer_netsuite_contact_id,
    customer_netsuite_payment_terms_id:
      state.customer_netsuite_payment_terms_id,
    customer_netsuite_price_level_id: state.customer_netsuite_price_level_id,
    customer_netsuite_price_level_discount:
      state.customer_netsuite_price_level_discount
  },
  lines: (state.items ?? []).map((item, i) =>
    toQuoteDraftLine(item as LineItem, i)
  )
});

/**
 * Creates the PSQL draft on first save, updates it (replace-all lines) after.
 * API-minted line uuids are merged back onto the store items by position.
 */
export const saveDraft = async (
  context: OrderContext
): Promise<QuoteDraft> => {
  const state = context.state;
  if (!state.items || state.items.length === 0) {
    throw new Error("Cannot save an empty quote draft.");
  }
  const payload = buildDraftPayload(state);
  const draft = state.draft_id
    ? await quoteDraftService.updateQuote(state.draft_id, payload)
    : await quoteDraftService.createQuote(payload);
  context.commit(OrderMutations.SetDraftMeta, {
    draft_id: draft.id,
    draft_status: draft.status,
    // Coerced so a response that omits the key clears the link rather than
    // leaving a stale one: SetDraftMeta skips `undefined`, but not `null`.
    netsuite_id: draft.netsuite_quote_id ?? null
  });
  context.commit(OrderMutations.SetDraftLineIds, draft.lines ?? []);
  return draft;
};

/** Hydrates the store from a PSQL draft (or an already-fetched one). */
export const loadDraft = async (
  context: OrderContext,
  value: number | QuoteDraft
): Promise<QuoteDraft> => {
  const draft =
    typeof value === "number"
      ? await quoteDraftService.getQuote(value)
      : value;
  const header = draft.header_payload ?? {};
  await clearOrder(context);
  context.commit(OrderMutations.SetOrder, {
    _id: null,
    // Stripped on the way in: the store holds the rep's own text only, so a
    // composed string from PSQL/NetSuite must not land in their textarea.
    notes: stripQuoteNotes(header.notes),
    // A NS-only quote (adopt) seeds the customer but carries no quote name in the
    // header — its name is the NS quote's title. Fall back to it when the header
    // has none, matching how the drafts list already shows `d.title` as the name.
    contact_details: {
      ...(header.contact_details ?? {}),
      quote_name: header.contact_details?.quote_name || draft.title
    } as Order["contact_details"],
    shipping_details: header.shipping_details,
    billing_details: header.billing_details,
    payment_details: header.payment_details
  } as Order);
  context.commit(OrderMutations.SetCustomer, {
    customer_id: header.customer_id ?? "",
    customer_name: header.customer_name ?? "",
    customer_netsuite_id: draft.netsuite_customer_id ?? undefined,
    customer_netsuite_contact_id:
      header.customer_netsuite_contact_id ?? undefined,
    customer_netsuite_payment_terms_id:
      header.customer_netsuite_payment_terms_id ?? undefined,
    customer_netsuite_price_level_id:
      header.customer_netsuite_price_level_id ?? undefined,
    customer_netsuite_price_level_discount:
      header.customer_netsuite_price_level_discount ?? undefined,
    order_id: null
  });
  // FUPOF-209: restore the last-known NetSuite shipping + tax. `clearOrder` above
  // nulled them, so a draft saved without them (or one never submitted) correctly
  // stays TBD; a submitted quote's reload then refreshes whatever is stored here.
  context.commit(OrderMutations.SetNsTotals, header.ns_totals ?? null);
  // FUPOF-191: this path sets the customer by committing the mutation directly
  // rather than through assignCustomer, so it has to warm the checkout's
  // customer-details fetch itself. Without it, opening a saved quote for
  // editing lands on the Cart with nothing prefetched and Quote Details is
  // back to a cold, overlay-blocked read.
  prefetchCustomerDetails(draft.netsuite_customer_id ?? undefined);
  context.commit(
    OrderMutations.SetAllLineItems,
    (draft.lines ?? []).map(toLineItem)
  );
  context.commit(OrderMutations.SetDraftMeta, {
    draft_id: draft.id,
    draft_status: draft.status,
    netsuite_id: draft.netsuite_quote_id ?? null
  });
  return draft;
};

export const deleteDraft = async (
  context: OrderContext,
  id: number
): Promise<void> => {
  await quoteDraftService.deleteQuote(id);
  if (context.state.draft_id === id) {
    context.commit(OrderMutations.SetDraftMeta, {
      draft_id: null,
      draft_status: null,
      netsuite_id: null
    });
  }
};

/**
 * FUPOF-191: line-notes drift is parked, so drop it from the report as it
 * arrives — before `hasLineDrift` below decides whether there is anything to
 * resolve, so a line that drifted on notes alone stops raising the resolver.
 *
 * A line's `notes` is the rep's free text; NetSuite's `custcol_productionnotes`
 * is the composed string (free text plus the configurator's per-field notes).
 * They are different shapes, so the API reports drift on every line that has
 * any configurator notes at all — which is nearly all of them. Making them
 * comparable is a real change (a mirror column alongside `notes`, so a composed
 * value never lands back in one of its own inputs) and is deliberately deferred.
 *
 * To restore: delete this function and its call. The API still sends the drift;
 * only the client ignores it.
 */
const stripParkedLineNotesDrift = (report: DriftReport): void => {
  for (const line of report.lines ?? []) {
    line.drift = (line.drift ?? []).filter(field => field !== "notes");
  }
};

/**
 * Re-fetches NS state for the loaded submitted quote and stores the drift
 * report. Header drift is applied immediately — NS always wins the header
 * (interview decision D4); only lines go through the DriftResolver UI.
 */
export const reloadFromNs = async (
  context: OrderContext
): Promise<DriftReport> => {
  const state = context.state;
  if (!state.draft_id) {
    throw new Error("No saved quote loaded to reload from NetSuite.");
  }
  const report = await quoteDraftService.reloadQuote(state.draft_id);
  stripParkedLineNotesDrift(report);
  const header = report.header_drift;
  if (header) {
    if (header.contact_details) {
      context.commit(OrderMutations.SetContact, header.contact_details);
    }
    if (header.shipping_details) {
      context.commit(OrderMutations.SetShipping, header.shipping_details);
    }
    if (header.billing_details) {
      context.commit(OrderMutations.SetBilling, header.billing_details);
    }
    if (header.payment_details) {
      context.commit(OrderMutations.SetPayment, header.payment_details);
    }
    if (header.notes !== undefined) {
      // NS wins the header, but the warning is derived — accepting it verbatim
      // would put a composed string back in the rep's textarea, and the next
      // compose would then warn about the warning.
      context.commit(OrderMutations.SetNotes, stripQuoteNotes(header.notes));
    }
    if (header.customer_id !== undefined) {
      // NS owns the customer link (decision D4): the API sends the full customer
      // block here only when the entity drifted from the draft's stored customer.
      // Adopt it verbatim, mirroring loadDraft, pulling the netsuite id from the
      // report's always-present top-level entity. order_id is preserved so a
      // reload never disturbs the loaded order's identity.
      context.commit(OrderMutations.SetCustomer, {
        customer_id: header.customer_id ?? "",
        customer_name: header.customer_name ?? "",
        customer_netsuite_id: report.netsuite_customer_id ?? undefined,
        customer_netsuite_contact_id:
          header.customer_netsuite_contact_id ?? undefined,
        customer_netsuite_payment_terms_id:
          header.customer_netsuite_payment_terms_id ?? undefined,
        customer_netsuite_price_level_id:
          header.customer_netsuite_price_level_id ?? undefined,
        customer_netsuite_price_level_discount:
          header.customer_netsuite_price_level_discount ?? undefined,
        order_id: context.state._id ?? null
      });
      // Direct mutation commit again — and here the customer actually changed
      // (NS drifted from the draft), so the cached details are for the wrong
      // entity. Re-point the prefetch at the customer NS says owns the quote.
      prefetchCustomerDetails(report.netsuite_customer_id ?? undefined);
    }
  }
  // FUPOF-209: NetSuite's shipping and tax, stored whether or not anything drifted —
  // the Quote Summary shows them on every reopened quote, not only a drifted one. An
  // older API omits the key entirely; that reads as unknown, same as a failed read.
  context.commit(OrderMutations.SetNsTotals, report.ns_totals ?? null);
  const hasLineDrift =
    (report.lines ?? []).some(l => l.drift.length > 0) ||
    (report.removed_lines ?? []).length > 0;
  context.commit(OrderMutations.SetDriftReport, hasLineDrift ? report : null);
  return report;
};

export const clearDriftReport = (context: OrderContext) => {
  context.commit(OrderMutations.SetDriftReport, null);
};

/**
 * The submit response returns the persisted draft with NetSuite identity filled
 * in: netsuite_quote_id on the draft, and ns_line_id on each line (previously
 * null until a reload). Merge it straight back so the store's lines carry NS
 * identity without a round-trip. Lines merge by array index / ascending
 * position, matching the API's ordering contract.
 */
export const applySubmittedDraft = (
  context: OrderContext,
  draft: QuoteDraft
) => {
  context.commit(OrderMutations.SetDraftMeta, {
    draft_id: draft.id,
    draft_status: draft.status,
    netsuite_id: draft.netsuite_quote_id ?? null
  });
  const contactId =
    draft.header_payload?.customer_netsuite_contact_id ??
    draft.header_payload?.contact_details?.contact_id ??
    null;
  if (contactId) {
    // The submit resolved the contact — either the one that was picked, or the
    // Contact the RESTlet just created from the typed-in details. Take the id
    // back so this order carries it (and, on a duplicate, hands it to the copy)
    // instead of looking like a quote with no contact chosen.
    context.commit(OrderMutations.SetCustomerContact, contactId);
  }
  context.commit(OrderMutations.SetDraftLineIds, draft.lines ?? []);
  // FUPOF-191: submitting can add a contact to the customer in NetSuite — a
  // quote for someone not yet in NetSuite is exactly how new contacts get
  // created — so everything cached about this customer now predates that
  // contact. Re-read them in the background: the rep's next quote for the same
  // customer, or an edit or duplicate of this one, has to be able to select the
  // person they just entered, and without this the Select Contact list stays as
  // it was for the rest of the session.
  refreshCustomerDetails(context.state.customer_netsuite_id);
};

/**
 * Carries the NetSuite document number (e.g. "QUO2400") into the store when
 * editing a submitted quote — it isn't on the PSQL draft, only on the NS quote
 * row the edit flow started from. Used by the edit-mode banner.
 */
export const setNetSuiteTranId = (
  context: OrderContext,
  tranId: string | null
) => {
  context.commit(OrderMutations.SetNetSuiteTranId, tranId);
};

/**
 * Strips draft/NS identity from the loaded order so it becomes a fresh,
 * unsaved cart copy (duplicate-quote flow). Line configurations keep their
 * content; the API will content-match them back to the same
 * line_configurations rows on the next save.
 */
export const markAsDuplicate = (context: OrderContext) => {
  const state = context.state;
  context.commit(OrderMutations.SetDraftMeta, {
    draft_id: null,
    draft_status: null,
    netsuite_id: null
  });
  // Name the copy so it reads as distinct from its source. The source's real
  // title arrives verbatim (the preview/draft hands it over unrenamed); the
  // rename belongs on the copy, and since `title` is derived from `quote_name`
  // this carries through to the cart header and the first Save Draft.
  const sourceName = state.contact_details?.quote_name ?? "";
  context.commit(OrderMutations.SetContact, {
    quote_name: `${sourceName} Duplicate`.trim()
  } as OrderDetails["contact_details"]);
  context.commit(
    OrderMutations.SetAllLineItems,
    ((state.items ?? []) as LineItem[]).map(item => {
      const copy = { ...item };
      delete copy.uuid;
      delete copy.ns_line_id;
      return copy;
    })
  );
  context.commit(OrderMutations.SetDriftReport, null);
  // FUPOF-209: a duplicate must NOT inherit the source's freight or tax. It is a new
  // quote with no NetSuite record, and both figures are recalculated in NetSuite per
  // quote — freight is re-quoted for this shipment, and AvaTax recomputes from the new
  // estimate's lines and ship-to. Carrying them over would show a rep a total that no
  // NetSuite record backs. This runs AFTER `loadDraft` in `duplicateQuote`, which
  // rehydrates the source's stored `ns_totals`, so it is what clears them; keep the
  // order if that flow is ever rearranged. The copy's first Save Draft then persists
  // null, and the Quote Summary shows TBD until this quote is submitted and reloaded.
  context.commit(OrderMutations.SetNsTotals, null);
  context.commit(
    OrderMutations.SetOrder,
    ({ created_type: CreatedType.Duplicated } as unknown) as Order
  );
};
